import { INITIAL_F2F_OPTIONAL_SECTION_KEYS } from "./initial-f2f-required-constant";
import {
  NOTE_CONTENT_SECTION_KEYS,
  NOTE_CPT_CODES_ANSWER_KEY,
  NOTE_CPT_FREE_TEXT_ANSWER_KEY,
  NOTE_DIAGNOSIS_CODES_ANSWER_KEY,
  NOTE_FREE_TEXT_ANSWER_KEY,
} from "./initial-f2f-constant";
import { NoteAnswer, NoteSection } from "./initial-f2f-types";

/** Sections whose answers live in the patient chart rather than the note content. */
/** Optional sections may stay empty; every other section must be answered before signing. */
export const isRequiredSectionKey = (sectionKey: string): boolean =>
  !(INITIAL_F2F_OPTIONAL_SECTION_KEYS as readonly string[]).includes(
    sectionKey
  );

export interface ChartBackedAnswers {
  medicalHistory: boolean;
  surgicalHistory: boolean;
  familyHistory: boolean;
  allergy: boolean;
  medication: boolean;
  socialHistory: boolean;
  reviewOfSystems: boolean;
  physicalExam: boolean;
}

export interface AnsweredContext {
  noteContent?: Record<string, object>;
  chiefComplaint: string;
  chart: ChartBackedAnswers;
}

const sectionOf = (
  noteContent: Record<string, object> | undefined,
  sectionKey: string
): NoteSection | undefined => {
  const contentKey =
    NOTE_CONTENT_SECTION_KEYS[
      sectionKey as keyof typeof NOTE_CONTENT_SECTION_KEYS
    ];
  const sections = (noteContent?.sections ?? {}) as Record<
    string,
    NoteSection | undefined
  >;
  return contentKey ? sections[contentKey] : undefined;
};

// CPT answers are stored under Plan of Care but belong to the Procedure Codes section.
const NON_SELECTION_ANSWER_KEYS = [
  NOTE_FREE_TEXT_ANSWER_KEY,
  NOTE_CPT_CODES_ANSWER_KEY,
  NOTE_CPT_FREE_TEXT_ANSWER_KEY,
];

const answersOf = (section?: NoteSection): [string, NoteAnswer][] =>
  Object.entries(section?.answers ?? {}).filter(
    ([key]) => !NON_SELECTION_ANSWER_KEYS.includes(key)
  );

/** An option or row answer counts when something was picked, not merely typed. */
const hasSelection = (answer: NoteAnswer): boolean =>
  (answer.selected?.length ?? 0) > 0 ||
  answer.value === true ||
  (typeof answer.value === "string" && answer.value.trim().length > 0);

const anySelection = (section?: NoteSection): boolean =>
  answersOf(section).some(([, answer]) => hasSelection(answer));

const hasCodes = (section: NoteSection | undefined, key: string): boolean =>
  (section?.answers?.[key]?.codes?.length ?? 0) > 0;

/** Whether a section meets its "mandatory" rule from the Premier MD requirements sheet. */
export const isSectionAnswered = (
  sectionKey: string,
  { noteContent, chiefComplaint, chart }: AnsweredContext
): boolean => {
  const section = sectionOf(noteContent, sectionKey);
  switch (sectionKey) {
    case "chiefComplaints":
      return chiefComplaint.trim().length > 0;
    case "hpi":
      return (section?.text ?? "").trim().length > 0;
    case "medicalHistory":
    case "surgicalHistory":
    case "familyHistory":
    case "allergy":
    case "medication":
    case "socialHistory":
    case "reviewOfSystems":
    case "physicalExam":
      return chart[sectionKey];
    case "diagnosis":
      return hasCodes(section, NOTE_DIAGNOSIS_CODES_ANSWER_KEY);
    case "procedureCodes":
      return hasCodes(
        sectionOf(noteContent, "planOfCare"),
        NOTE_CPT_CODES_ANSWER_KEY
      );
    default:
      // Row, vitals and checkbox sections: any ticked option or filled vital.
      return anySelection(section);
  }
};
